/**
 * StepHistory - Dashboard historique & progression Go Sport
 * Affiche les sessions passées, le streak, la progression du niveau,
 * et des statistiques globales.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, Flame, Clock, Target, TrendingUp, Calendar, Star,
  ChevronDown, ChevronUp, Zap, Dumbbell, BarChart3, Sparkles, Loader2
} from 'lucide-react';
import { SPORT_LEVELS } from '../../data/levels';
import goSportAI from '../../services';

export function StepHistory({ stats, sessions, level, streak, profile, onClose, onStartSession }) {
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const sortedSessions = useMemo(() => {
    return [...(sessions || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [sessions]);

  const displayedSessions = showAllSessions ? sortedSessions : sortedSessions.slice(0, 5);

  const levelInfo = SPORT_LEVELS.find(l => l.level === level) || SPORT_LEVELS[0];

  // Weekly stats (last 7 days)
  const weeklyStats = useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const weekSessions = (sessions || []).filter(s => new Date(s.date) >= weekAgo);
    return {
      count: weekSessions.length,
      duration: weekSessions.reduce((t, s) => t + (s.duration || 0), 0),
      calories: weekSessions.reduce((t, s) => t + (s.caloriesEstimated || 0), 0),
    };
  }, [sessions]);

  // Monthly stats (last 30 days)
  const monthlyStats = useMemo(() => {
    const now = new Date();
    const monthAgo = new Date(now);
    monthAgo.setDate(monthAgo.getDate() - 30);

    const monthSessions = (sessions || []).filter(s => new Date(s.date) >= monthAgo);
    return {
      count: monthSessions.length,
      duration: monthSessions.reduce((t, s) => t + (s.duration || 0), 0),
      calories: monthSessions.reduce((t, s) => t + (s.caloriesEstimated || 0), 0),
    };
  }, [sessions]);

  // Level progress bar (based on session count milestones)
  const levelProgress = useMemo(() => {
    const totalSessions = (sessions || []).length;
    // Milestones per level: level × 5 sessions to "master" a level
    const sessionsForCurrentLevel = level * 5;
    const progress = Math.min(100, Math.round((totalSessions / sessionsForCurrentLevel) * 100));
    return progress;
  }, [sessions, level]);

  // Auto-fetch AI analysis if enough sessions
  const fetchAIAnalysis = async () => {
    if (aiLoading || (sessions || []).length < 2) return;
    setAiLoading(true);
    try {
      const result = await goSportAI.analyzeProgression(
        profile || {},
        sessions,
        stats,
        streak
      );
      if (result) setAiAnalysis(result);
    } catch {
      // Silently fail
    } finally {
      setAiLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    return `${m} min`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-4 py-4 space-y-5"
    >
      {/* Header */}
      <div className="text-center space-y-1">
        <div className="flex items-center justify-center gap-2">
          <BarChart3 className="w-6 h-6 text-orange-400" />
          <h3 className="text-xl font-bold text-white">Ma Progression</h3>
        </div>
        <p className="text-sm text-zinc-500">Historique et statistiques</p>
      </div>

      {/* Level Card */}
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-gradient-to-br from-orange-500/20 to-red-500/20
          border border-orange-500/30 rounded-2xl p-4 space-y-3"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{levelInfo.emoji}</span>
            <div>
              <p className="text-lg font-bold text-white">Niveau {level}</p>
              <p className="text-xs text-orange-300">{levelInfo.label}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black text-orange-400">{streak?.current || 0}</p>
            <p className="text-[10px] text-zinc-500 flex items-center gap-1 justify-end">
              <Flame className="w-3 h-3 text-yellow-400" /> streak
            </p>
          </div>
        </div>

        {/* Level progress bar */}
        <div>
          <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
            <span>Progression</span>
            <span>{levelProgress}%</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${levelProgress}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="h-full bg-gradient-to-r from-orange-500 to-yellow-400 rounded-full"
            />
          </div>
          {streak?.best > 0 && (
            <p className="text-[10px] text-zinc-600 mt-1">
              Meilleur streak : {streak.best} jours
            </p>
          )}
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div>
        <p className="text-xs font-medium text-zinc-500 mb-2 uppercase tracking-wider">Cette semaine</p>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 text-center">
            <Target className="w-4 h-4 text-orange-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{weeklyStats.count}</p>
            <p className="text-[10px] text-zinc-500">Séances</p>
          </div>
          <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 text-center">
            <Clock className="w-4 h-4 text-blue-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{Math.round(weeklyStats.duration / 60)}</p>
            <p className="text-[10px] text-zinc-500">Minutes</p>
          </div>
          <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 text-center">
            <Flame className="w-4 h-4 text-red-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{weeklyStats.calories}</p>
            <p className="text-[10px] text-zinc-500">kcal</p>
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs font-medium text-zinc-500 mb-2 uppercase tracking-wider">Ce mois</p>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 text-center">
            <Target className="w-4 h-4 text-orange-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{monthlyStats.count}</p>
            <p className="text-[10px] text-zinc-500">Séances</p>
          </div>
          <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 text-center">
            <Clock className="w-4 h-4 text-blue-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{Math.round(monthlyStats.duration / 60)}</p>
            <p className="text-[10px] text-zinc-500">Minutes</p>
          </div>
          <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 text-center">
            <Flame className="w-4 h-4 text-red-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{monthlyStats.calories}</p>
            <p className="text-[10px] text-zinc-500">kcal</p>
          </div>
        </div>
      </div>

      {/* All-time stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 flex items-center gap-3">
          <Trophy className="w-5 h-5 text-yellow-400 shrink-0" />
          <div>
            <p className="text-sm font-bold text-white">{stats?.totalSessions || 0}</p>
            <p className="text-[10px] text-zinc-500">Séances totales</p>
          </div>
        </div>
        <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 flex items-center gap-3">
          <Star className="w-5 h-5 text-yellow-400 shrink-0" />
          <div>
            <p className="text-sm font-bold text-white">{stats?.avgRating || '—'}</p>
            <p className="text-[10px] text-zinc-500">Note moyenne</p>
          </div>
        </div>
      </div>

      {/* AI Coach Analysis */}
      {(sessions || []).length >= 2 && (
        <div className="border border-purple-500/30 rounded-xl overflow-hidden">
          {!aiAnalysis && !aiLoading ? (
            <button
              onClick={fetchAIAnalysis}
              className="w-full px-4 py-3 flex items-center justify-center gap-2
                bg-purple-500/10 text-purple-300 text-sm font-medium
                hover:bg-purple-500/20 transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Analyse IA de ma progression
            </button>
          ) : aiLoading ? (
            <div className="px-4 py-4 flex items-center justify-center gap-2 text-purple-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Analyse en cours…
            </div>
          ) : aiAnalysis ? (
            <div className="px-4 py-3 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-purple-300">Coach IA</span>
              </div>

              <p className="text-sm text-zinc-300">{aiAnalysis.analysis}</p>

              {aiAnalysis.strengths?.length > 0 && (
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Points forts</p>
                  {aiAnalysis.strengths.map((s, i) => (
                    <p key={i} className="text-xs text-green-400 flex items-center gap-1">
                      <span className="text-green-500">+</span> {s}
                    </p>
                  ))}
                </div>
              )}

              {aiAnalysis.improvements?.length > 0 && (
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">À améliorer</p>
                  {aiAnalysis.improvements.map((s, i) => (
                    <p key={i} className="text-xs text-yellow-400 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> {s}
                    </p>
                  ))}
                </div>
              )}

              {aiAnalysis.nextGoal && (
                <div className="bg-purple-500/10 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-purple-400 uppercase tracking-wider mb-0.5">Prochain objectif</p>
                  <p className="text-xs text-purple-200">{aiAnalysis.nextGoal}</p>
                </div>
              )}

              {aiAnalysis.motivationTip && (
                <p className="text-xs text-zinc-400 italic">💡 {aiAnalysis.motivationTip}</p>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Session History */}
      <div className="border border-zinc-700/50 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-zinc-800/40">
          <span className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-zinc-500" />
            Historique des séances
          </span>
          <span className="text-xs text-zinc-600">{sortedSessions.length} total</span>
        </div>

        {sortedSessions.length === 0 ? (
          <div className="px-4 py-6 text-center">
            <Dumbbell className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">Aucune séance enregistrée</p>
            <p className="text-xs text-zinc-600 mt-1">Lance ta première séance !</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            <AnimatePresence>
              {displayedSessions.map((s, i) => (
                <motion.div
                  key={s.id || i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="px-4 py-2.5 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20
                      flex items-center justify-center">
                      <Zap className="w-4 h-4 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm text-zinc-300">Niv. {s.level}</p>
                      <p className="text-[10px] text-zinc-600">{formatDate(s.date)}</p>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    <div>
                      <p className="text-xs text-zinc-400">{formatDuration(s.duration || 0)}</p>
                      <p className="text-[10px] text-zinc-600">~{s.caloriesEstimated || 0} kcal</p>
                    </div>
                    {s.rating > 0 && (
                      <div className="flex items-center gap-0.5">
                        <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        <span className="text-xs text-yellow-400">{s.rating}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {sortedSessions.length > 5 && (
              <button
                onClick={() => setShowAllSessions(!showAllSessions)}
                className="w-full px-4 py-2 text-xs text-zinc-500 hover:text-zinc-300
                  flex items-center justify-center gap-1 transition-colors"
              >
                {showAllSessions ? (
                  <><ChevronUp className="w-3 h-3" /> Réduire</>
                ) : (
                  <><ChevronDown className="w-3 h-3" /> Voir les {sortedSessions.length - 5} autres</>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 pt-2">
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={onStartSession}
          className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500
            text-white font-bold flex items-center justify-center gap-2
            shadow-lg shadow-orange-500/20"
        >
          <Zap className="w-5 h-5" />
          Lancer une séance
        </motion.button>

        <button
          onClick={onClose}
          className="w-full py-3 rounded-xl bg-zinc-800 border border-zinc-700
            text-zinc-400 font-medium text-sm"
        >
          Retour
        </button>
      </div>
    </motion.div>
  );
}
